/**
 * VerifyEmailBanner
 *
 * Signup does not block on confirmation, so this is how the app asks. It sits
 * above the page for accounts that have not confirmed yet, offers to resend,
 * and can be dismissed for the session: a prompt that cannot be silenced
 * stops being a prompt and becomes furniture.
 *
 * Nothing is withheld while it shows. Alerts still fire, the portfolio still
 * works. It exists because an unconfirmed address is one we cannot reach, not
 * as leverage.
 */

import React, { useState } from 'react';
import { Box, Button, Typography, Collapse, IconButton } from '@mui/material';
import { Icon } from '@iconify/react';
import { useAuth } from '../context/AuthContext';
import { useThemeMode } from '../context/ThemeContext';
import CorrectEmailDialog from './CorrectEmailDialog';
import { resendVerification } from '../services/auth';

const DISMISS_KEY = 'farmgate-verify-banner-dismissed';

function VerifyEmailBanner() {
  const { user, isAuthenticated } = useAuth();
  const { palette } = useThemeMode();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const [state, setState] = useState('idle'); // idle | sending | sent | failed
  const [fixing, setFixing] = useState(false);   // the wrong-address dialog

  // emailVerified is absent on sessions issued before this shipped; treating
  // undefined as verified keeps the banner off for people it cannot help.
  const unverified = isAuthenticated && user?.emailVerified === false;
  if (!unverified || dismissed) return null;

  const dismiss = () => {
    setDismissed(true);
    try {
      sessionStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* private browsing */
    }
  };

  const resend = async () => {
    setState('sending');
    try {
      await resendVerification();
      setState('sent');
    } catch {
      setState('failed');
    }
  };

  return (
    <Collapse in>
      <Box
        role="status"
        sx={{
          display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap',
          px: { xs: 2, md: 3 }, py: 1.25,
          bgcolor: palette.warningBg,
          borderBottom: `1px solid ${palette.warning}33`,
        }}
      >
        <Icon icon="ph:envelope-simple" width={18} height={18} color={palette.warning} />
        <Typography sx={{ fontSize: '0.85rem', color: palette.text, flex: 1, minWidth: 200 }}>
          {state === 'sent' ? (
            <>A new confirmation link is on its way to <strong>{user.email}</strong>.</>
          ) : state === 'failed' ? (
            <>We could not send that just now. Please try again in a moment.</>
          ) : (
            <>Confirm <strong>{user.email}</strong> so we can reach you about your alerts.</>
          )}
        </Typography>
        {state !== 'sent' && (
          <Button
            size="small"
            onClick={resend}
            disabled={state === 'sending'}
            sx={{ textTransform: 'none', fontWeight: 700, color: palette.warning }}
          >
            {state === 'sending' ? 'Sending…' : 'Resend the link'}
          </Button>
        )}
        <Button
          size="small"
          onClick={() => setFixing(true)}
          sx={{ textTransform: 'none', fontWeight: 700, color: palette.warning }}
        >
          Wrong address?
        </Button>
        <IconButton size="small" onClick={dismiss} aria-label="Dismiss until next sign-in" sx={{ color: palette.textMuted }}>
          <Icon icon="ph:x" width={15} height={15} />
        </IconButton>
      </Box>
      <CorrectEmailDialog
        open={fixing}
        onClose={() => setFixing(false)}
        onCorrected={() => setState('sent')}
      />
    </Collapse>
  );
}

export default VerifyEmailBanner;
